import { Check, ChevronDown, Info, LogOut, Moon, Plus, Settings, Sun, User, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { AboutDialog } from '@/components/regulatory/about-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant, useTheme } from '@/context';
import { useCurrentTenant, useTenantList } from '@/context/tenant-registry-context';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useSignOut } from '@/hooks/use-sign-out';
import type { TenantRegistryEntry } from '@/lib/tenant-registry';

export function TenantMenu() {
    const navigate = useNavigate();
    const { selectTenant } = useTenant();
    const { entry: currentTenant } = useCurrentTenant();
    // The switcher lists what the regional APIs answered, merged with the token
    // claim for regions that have not answered yet — the claim alone would show
    // stale names and miss a brand new organization (ESCLOUD-268).
    const { entries, isLoading: registryLoading } = useTenantList();
    const signOut = useSignOut();
    const { theme, setTheme, resolvedTheme } = useTheme();
    const { data: user, isLoading: userLoading } = useCurrentUser();
    const [aboutOpen, setAboutOpen] = useState(false);

    const isLoading = registryLoading || userLoading;

    const toggleTheme = () => {
        if (theme === 'system') {
            setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
        } else {
            setTheme(theme === 'dark' ? 'light' : 'dark');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-4 w-24" />
            </div>
        );
    }

    const handleSelectTenant = (entry: TenantRegistryEntry) => {
        selectTenant(entry.tenant.id);
        navigate(`/tenants/${entry.tenant.id}`);
    };

    const handleLogout = () => {
        signOut();
    };

    const tenantInitial = currentTenant?.tenant.name.charAt(0).toUpperCase() ?? 'E';
    const tenantName = currentTenant?.tenant.name ?? 'Select Organization';
    const userName = user?.name ?? user?.email ?? 'User';

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-auto gap-2 px-2 py-1.5"
                        data-testid="tenant-menu-trigger"
                    >
                        <Avatar className="h-8 w-8 rounded-md">
                            <AvatarFallback className="rounded-md bg-primary text-primary-foreground text-sm font-medium">
                                {tenantInitial}
                            </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{tenantName}</span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                    {/* Tenant Info Header */}
                    <DropdownMenuLabel className="font-normal">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 rounded-md">
                                <AvatarFallback className="rounded-md bg-primary text-primary-foreground font-medium">
                                    {tenantInitial}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                                <span className="font-medium">{tenantName}</span>
                                {currentTenant && (
                                    <span className="text-xs text-muted-foreground capitalize">
                                        {currentTenant.role}
                                    </span>
                                )}
                            </div>
                        </div>
                    </DropdownMenuLabel>

                    {/* Settings */}
                    {currentTenant && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => navigate(`/settings/tenant/${currentTenant.tenant.id}`)}
                                className="cursor-pointer"
                            >
                                <Settings className="mr-2 h-4 w-4" />
                                Settings
                            </DropdownMenuItem>
                        </>
                    )}

                    {/* Account Switcher Submenu */}
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            <Users className="mr-2 h-4 w-4" />
                            Switch Account
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                            <DropdownMenuSubContent className="w-56">
                                {entries.map((entry) => (
                                    <DropdownMenuItem
                                        key={entry.tenant.id}
                                        onClick={() => handleSelectTenant(entry)}
                                        className="cursor-pointer"
                                    >
                                        <div className="flex h-6 w-6 items-center justify-center rounded bg-muted text-xs font-medium mr-2">
                                            {entry.tenant.name.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="flex-1 truncate">{entry.tenant.name}</span>
                                        <Badge variant="outline" className="ml-2 text-xs font-normal">
                                            {entry.tenant.region.toUpperCase()}
                                        </Badge>
                                        {currentTenant?.tenant.id === entry.tenant.id && (
                                            <Check className="h-4 w-4 ml-2" />
                                        )}
                                    </DropdownMenuItem>
                                ))}
                                {entries.length === 0 && (
                                    <DropdownMenuItem disabled>
                                        <span className="text-muted-foreground">No organizations</span>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate('/subscribe/account')}
                                    className="cursor-pointer"
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Create Workspace
                                </DropdownMenuItem>
                            </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                    </DropdownMenuSub>

                    {/* User Settings */}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        {userName}
                    </DropdownMenuItem>

                    {/* Theme Toggle */}
                    <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
                        {resolvedTheme === 'dark' ? (
                            <Sun className="mr-2 h-4 w-4" />
                        ) : (
                            <Moon className="mr-2 h-4 w-4" />
                        )}
                        {resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </DropdownMenuItem>

                    {/* Regulatory labelling dialog (ESCLOUD-76) */}
                    <DropdownMenuItem onClick={() => setAboutOpen(true)} className="cursor-pointer">
                        <Info className="mr-2 h-4 w-4" />
                        About ESCloud
                    </DropdownMenuItem>

                    {/* Sign Out */}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                    </DropdownMenuItem>

                    {/* Application version info */}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-light font-mono">
                        Version : {__APP_VERSION__}
                    </DropdownMenuLabel>
                </DropdownMenuContent>
            </DropdownMenu>
            <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
        </>
    );
}
